// Weather: gusts that move the wind mid-flight, and the precipitation that makes
// the sky look like it means it.
//
// There is no separate wind here — a gust only has to move `state.wind`, because
// the banana already reads that number every single frame. Rain is likewise
// pushed by the same wind that pushes the banana, so what you see falling is
// also what is dragging your shot sideways.

import { W, H } from './canvas.js';
import { state } from './state.js';
import { clamp, rand } from './utils.js';
import { mods } from './modes.js';
import {
  GUST_EVERY_MAX_MS,
  GUST_EVERY_MIN_MS,
  GUST_STRENGTH,
  RAIN_DROPS,
  RAIN_SPEED,
  SNOW_DRIFT,
  SNOW_FLAKES,
  SNOW_SPEED,
} from './config.js';

/** How hard the wind leans on the rain, versus on a banana. */
const RAIN_WIND = 0.07;

/** Particles to keep alive for a given kind of weather. */
function density(kind) {
  if (kind === 'snow') return SNOW_FLAKES;
  if (kind === 'rain') return RAIN_DROPS;
  return 0;
}

/** Rebuilds the field only when the kind changes, so it survives a resize of nothing. */
function seed(kind) {
  if (state.precipKind === kind && state.precip.length === density(kind)) return;

  state.precipKind = kind;
  state.precip = Array.from({ length: density(kind) }, () => ({
    x: rand(0, W),
    y: rand(0, H),
    // Depth, 4..10. Nearer particles fall faster and draw brighter, which is
    // the whole trick to stop it looking like a flat sheet.
    z: rand(4, 10),
  }));
}

/** Wind that changes its mind. Nudges, never teleports. */
function gust(now) {
  const m = mods();
  if (!m.gusts) return;

  if (!state.nextGust) {
    state.nextGust = now + rand(GUST_EVERY_MIN_MS, GUST_EVERY_MAX_MS);
    return;
  }

  if (now < state.nextGust) return;

  state.nextGust = now + rand(GUST_EVERY_MIN_MS, GUST_EVERY_MAX_MS);
  state.wind = clamp(state.wind + rand(-GUST_STRENGTH, GUST_STRENGTH), -m.windMax, m.windMax);
}

/** Called once per frame by the render loop, next to the antics. */
export function advanceWeather(now) {
  gust(now);

  const kind = mods().precipitation;
  seed(kind);
  if (!kind) return;

  const snow = kind === 'snow';
  const fall = snow ? SNOW_SPEED : RAIN_SPEED;

  for (const d of state.precip) {
    d.y += fall * (d.z / 8);
    // Snow wanders on its own; rain is dragged by whatever the wind is doing.
    d.x += snow ? SNOW_DRIFT * Math.sin(now / 900 + d.z) : state.wind * RAIN_WIND;

    if (d.y > H + 4) {
      d.y = -6;
      d.x = rand(0, W);
    }
    // Wrap sideways so a strong gust never empties one edge of the screen.
    if (d.x < -12) d.x = W + 12;
    if (d.x > W + 12) d.x = -12;
  }
}

/** Clears the sky, so switching modes does not leave yesterday's rain falling. */
export function resetWeather() {
  state.precip = [];
  state.precipKind = null;
  state.nextGust = 0;
}
